import React, { useState, useRef, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, RadiologyRequest } from '../../types';
import { Scan, CheckCircle, Upload, X } from 'lucide-react';
// Biometry rows for the obstetric structured form
const BIOMETRY_ROWS = ['GS', 'CRL', 'BPD', 'HC', 'AC', 'FL', 'EFW'];
type ObstetricForm = {
  numberOfFetuses: string;
  presentation: string;
  afiDvp: string;
  fhr: string;
  fetalAnatomy: string;
  fetalMovements: string;
  cervixInternalOs: string;
  placentaA: string;
  placentaB: string;
  fetalA: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  fetalB: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  impression: string;
  sonographerName: string;
};
type PelvicForm = {
  findings: string;
  sonographerName: string;
};
const blankBiometry = () => BIOMETRY_ROWS.reduce((acc, row) => {
  acc[row] = {
    size: '',
    ega: ''
  };
  return acc;
}, {} as {
  [key: string]: {
    size: string;
    ega: string;
  };
});
export const RadiologyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    rad: RadiologyRequest;
  } | null>(null);
  const [report, setReport] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [obstetricForm, setObstetricForm] = useState<ObstetricForm>({
    numberOfFetuses: '',
    presentation: '',
    afiDvp: '',
    fhr: '',
    fetalAnatomy: '',
    fetalMovements: '',
    cervixInternalOs: '',
    placentaA: '',
    placentaB: '',
    fetalA: blankBiometry(),
    fetalB: blankBiometry(),
    impression: '',
    sonographerName: ''
  });
  const [pelvicForm, setPelvicForm] = useState<PelvicForm>({
    findings: '',
    sonographerName: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Find encounters with pending radiology requests
  const pendingRequests = encounters.flatMap(enc => enc.radiologyRequests.filter(rad => rad.status === 'Pending').map(rad => ({
    enc,
    rad
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    rad: RadiologyRequest;
  }) => {
    setSelectedRequest(item);
    setReport('');
    setImages([]);
    setObstetricForm({
      numberOfFetuses: '',
      presentation: '',
      afiDvp: '',
      fhr: '',
      fetalAnatomy: '',
      fetalMovements: '',
      cervixInternalOs: '',
      placentaA: '',
      placentaB: '',
      fetalA: blankBiometry(),
      fetalB: blankBiometry(),
      impression: '',
      sonographerName: currentUser?.fullName || ''
    });
    setPelvicForm({
      findings: '',
      sonographerName: currentUser?.fullName || ''
    });
  };
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => {
        const img = new Image();
        img.onload = () => {
          // Downscale so the long edge is at most 1280px — keeps each image well under
          // ~150-250KB so multiple scans fit inside Firestore's 1MB document cap.
          const MAX_DIM = 1280;
          let {
            width,
            height
          } = img;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round(height * MAX_DIM / width);
              width = MAX_DIM;
            } else {
              width = Math.round(width * MAX_DIM / height);
              height = MAX_DIM;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas not supported'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => reject(new Error('Could not load image'));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Could not read file'));
      reader.readAsDataURL(file);
    });
  };
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.size > 50 * 1024 * 1024) {
        alert(`${file.name} exceeds 50MB limit`);
        continue;
      }
      try {
        const compressed = await compressImage(file);
        setImages(prev => [...prev, compressed]);
      } catch (err) {
        console.error('Failed to compress image:', err);
        alert(`Failed to process ${file.name}`);
      }
    }
    // Reset the input so the same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  const buildObstetricReport = (f: ObstetricForm): string => {
    const lines: string[] = [];
    lines.push('OBSTETRIC ULTRASOUND REPORT');
    lines.push('');
    lines.push('FINDINGS');
    lines.push(`Number of Fetuses/GS: ${f.numberOfFetuses}`);
    lines.push(`Presentation: ${f.presentation}`);
    lines.push(`AFI/DVP: ${f.afiDvp}`);
    lines.push(`FHR: ${f.fhr}`);
    lines.push(`Fetal Anatomy: ${f.fetalAnatomy}`);
    lines.push(`Fetal Movements: ${f.fetalMovements}`);
    lines.push(`Cervix/Internal OS: ${f.cervixInternalOs}`);
    lines.push('');
    lines.push('BIOMETRY');
    lines.push(`Placenta (Fetal A): ${f.placentaA}`);
    lines.push(`Placenta (Fetal B): ${f.placentaB}`);
    lines.push('');
    lines.push('FETAL A:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalA[row].size || '-'}, EGA=${f.fetalA[row].ega || '-'}`);
    });
    lines.push('');
    lines.push('FETAL B:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalB[row].size || '-'}, EGA=${f.fetalB[row].ega || '-'}`);
    });
    lines.push('');
    lines.push(`IMPRESSION: ${f.impression}`);
    lines.push('');
    lines.push(`SONOGRAPHER'S NAME: ${f.sonographerName}`);
    return lines.join('\n');
  };
  const buildPelvicReport = (f: PelvicForm): string => {
    return ['ULTRASOUND REPORT', '', 'FINDINGS:', f.findings, '', `SONOGRAPHER'S NAME: ${f.sonographerName}`].join('\n');
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan' || scanType === 'Abdominal Scan' || scanType === 'Abdomen/Pelvis Scan';
    let finalReport = report;
    if (isObstetric) {
      finalReport = buildObstetricReport(obstetricForm);
    } else if (isPelvic) {
      finalReport = buildPelvicReport(pelvicForm);
    }
    if (!finalReport.trim() && images.length === 0) {
      alert('Please complete the report or upload images before sending.');
      return;
    }
    const {
      enc,
      rad
    } = selectedRequest;
    const updatedRad: RadiologyRequest = {
      ...rad,
      status: 'Completed',
      report: finalReport,
      images,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id,
      radiographerName: currentUser.fullName
    };
    const updatedRadRequests = enc.radiologyRequests.map(r => r.id === rad.id ? updatedRad : r);
    // Guard against Firestore's 1MB document cap. If the payload (mostly base64
    // images) is too large, the write would fail silently and the report would
    // never reach the clinician.
    const payloadBytes = JSON.stringify(updatedRadRequests).length;
    if (payloadBytes > 900000) {
      alert(`The combined report + images are too large to send (${Math.round(payloadBytes / 1024)}KB). Remove one or more images and try again.`);
      return;
    }
    // Check if all rads are completed
    const allRadsCompleted = updatedRadRequests.every(r => r.status === 'Completed');
    const updateData: Partial<Encounter> = {
      radiologyRequests: updatedRadRequests,
      pendingRadiology: !allRadsCompleted
    };
    // If all rads done and no pending lab, move back to clinician
    if (allRadsCompleted && !enc.pendingLab) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Scan results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-5197">Patient not found</div>;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan' || scanType === 'Abdominal Scan' || scanType === 'Abdomen/Pelvis Scan';
    const isSendDisabled = !isObstetric && !isPelvic ? !report && images.length === 0 : false;
    return <div className="space-y-6" data-id="element-5198">
        <div className="flex justify-between items-center" data-id="element-5199">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-5200">
            Radiology Results Entry
          </h1>
          <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-5201">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-purple-50 border-purple-200" data-id="element-5202">
          <div className="flex justify-between items-start" data-id="element-5203">
            <div data-id="element-5204">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-5205">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-5206">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-5207">
              <Badge variant="purple" className="text-sm" data-id="element-5208">
                {selectedRequest.rad.scanType}
              </Badge>
              <p className="text-xs text-gray-500 mt-1" data-id="element-5209">
                Requested:{' '}
                {new Date(selectedRequest.rad.requestedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <div className={isObstetric || isPelvic ? 'space-y-6' : 'grid grid-cols-1 lg:grid-cols-2 gap-6'} data-id="element-5210">
          <Card title="Upload Images" data-id="element-5211">
            <div className="space-y-4" data-id="element-5212">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-5213">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" data-id="element-5214" />
                <p className="text-sm text-gray-600" data-id="element-5215">
                  Click to upload images (PNG/JPG)
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-5216">
                  Max size: 50MB per image
                </p>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/png, image/jpeg" multiple onChange={handleImageUpload} data-id="element-5217" />
              </div>

              {images.length > 0 && <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-5218">
                  {images.map((img, idx) => <div key={idx} className="relative group" data-id="element-5219">
                      <img src={img} alt={`Scan ${idx}`} className="h-24 w-full object-cover rounded border border-gray-200" data-id="element-5220" />
                      <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-5221">
                        <X className="w-4 h-4" data-id="element-5222" />
                      </button>
                    </div>)}
                </div>}
            </div>
          </Card>

          {/* Conditional report area */}
          {isObstetric ? <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-id="element-5223">
              {/* Form Header */}
              <div className="text-center border-b-2 border-black pb-4 mb-6" data-id="element-5224">
                <h2 className="font-bold text-xl uppercase tracking-wide text-gray-900" data-id="element-5225">
                  Obstetric Ultrasound Report
                </h2>
              </div>

              {/* FINDINGS */}
              <div className="mb-8" data-id="element-5226">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5227">
                  Findings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-5228">
                  {[{
                key: 'numberOfFetuses',
                label: 'Number of Fetuses / GS'
              }, {
                key: 'presentation',
                label: 'Presentation'
              }, {
                key: 'afiDvp',
                label: 'AFI / DVP'
              }, {
                key: 'fhr',
                label: 'FHR'
              }, {
                key: 'fetalAnatomy',
                label: 'Fetal Anatomy'
              }, {
                key: 'fetalMovements',
                label: 'Fetal Movements'
              }, {
                key: 'cervixInternalOs',
                label: 'Cervix / Internal OS'
              }].map(field => <div key={field.key} data-id="element-5229">
                      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5230">
                        {field.label}
                      </label>
                      <input type="text" value={(obstetricForm as any)[field.key]} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  [field.key]: e.target.value
                })} placeholder={`Enter ${field.label.toLowerCase()}`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5231" />
                    </div>)}
                </div>
              </div>

              {/* BIOMETRY — redesigned with explicit text boxes per value */}
              <div className="mb-8" data-id="element-5232">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5233">
                  Biometry
                </h3>

                {/* Placenta inputs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5234">
                  <div data-id="element-5235">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase" data-id="element-5236">
                      Placenta (Fetal A)
                    </label>
                    <input type="text" value={obstetricForm.placentaA} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  placentaA: e.target.value
                })} placeholder="Enter placenta details" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5237" />
                  </div>
                  <div data-id="element-5238">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase" data-id="element-5239">
                      Placenta (Fetal B)
                    </label>
                    <input type="text" value={obstetricForm.placentaB} onChange={e => setObstetricForm({
                  ...obstetricForm,
                  placentaB: e.target.value
                })} placeholder="Enter placenta details" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5240" />
                  </div>
                </div>

                {/* Fetal A / Fetal B biometry — each value gets visible text boxes */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4" data-id="element-5241">
                  {(['fetalA', 'fetalB'] as const).map(side => <div key={side} className="bg-purple-50/40 border border-purple-200 rounded-lg p-3" data-id="element-5242">
                      <p className="font-bold text-center mb-3 bg-purple-100 text-purple-900 py-1.5 rounded" data-id="element-5243">
                        {side === 'fetalA' ? 'FETAL A' : 'FETAL B'}
                      </p>

                      {/* Column labels */}
                      <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 mb-2 px-1" data-id="element-5244">
                        <span data-id="element-5245"></span>
                        <span className="text-xs font-bold text-gray-600 uppercase text-center" data-id="element-5246">
                          Size
                        </span>
                        <span className="text-xs font-bold text-gray-600 uppercase text-center" data-id="element-5247">
                          EGA
                        </span>
                      </div>

                      <div className="space-y-2" data-id="element-5248">
                        {BIOMETRY_ROWS.map(row => <div key={row} className="grid grid-cols-[3rem_1fr_1fr] gap-2 items-center" data-id="element-5249">
                            <label htmlFor={`${side}-${row}-size`} className="text-sm font-bold text-gray-800 text-center" data-id="element-5250">
                              {row}
                            </label>
                            <input id={`${side}-${row}-size`} type="text" value={obstetricForm[side][row].size} onChange={e => setObstetricForm({
                      ...obstetricForm,
                      [side]: {
                        ...obstetricForm[side],
                        [row]: {
                          ...obstetricForm[side][row],
                          size: e.target.value
                        }
                      }
                    })} placeholder="Size" className="w-full border-2 border-gray-300 rounded-md p-1.5 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5251" />
                            <input type="text" value={obstetricForm[side][row].ega} onChange={e => setObstetricForm({
                      ...obstetricForm,
                      [side]: {
                        ...obstetricForm[side],
                        [row]: {
                          ...obstetricForm[side][row],
                          ega: e.target.value
                        }
                      }
                    })} placeholder="EGA" className="w-full border-2 border-gray-300 rounded-md p-1.5 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5252" />
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>

              {/* IMPRESSION */}
              <div className="mb-8" data-id="element-5253">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5254">
                  Impression
                </h3>
                <textarea value={obstetricForm.impression} onChange={e => setObstetricForm({
              ...obstetricForm,
              impression: e.target.value
            })} placeholder="Enter clinical impression..." className="w-full p-3 min-h-[140px] text-sm border-2 border-gray-300 rounded-md focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5255" />
              </div>

              {/* Sonographer footer */}
              <div className="pt-6 border-t border-gray-300" data-id="element-5256">
                <div className="max-w-md" data-id="element-5257">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5258">
                    Sonographer's Name
                  </label>
                  <input type="text" value={obstetricForm.sonographerName} onChange={e => setObstetricForm({
                ...obstetricForm,
                sonographerName: e.target.value
              })} placeholder="Enter sonographer name" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5259" />
                </div>
              </div>
            </div> : isPelvic ? <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-id="element-5260">
              <div className="text-center border-b-2 border-black pb-4 mb-6" data-id="element-5261">
                <h2 className="font-bold text-xl uppercase tracking-wide text-gray-900" data-id="element-5262">
                  Ultrasound Report
                </h2>
                <p className="text-sm font-medium text-gray-600 mt-1" data-id="element-5263">
                  {scanType}
                </p>
              </div>
              <div className="mb-8" data-id="element-5264">
                <h3 className="font-bold text-lg mb-4 uppercase border-b border-gray-300 pb-2" data-id="element-5265">
                  Findings
                </h3>
                <textarea value={pelvicForm.findings} onChange={e => setPelvicForm({
              ...pelvicForm,
              findings: e.target.value
            })} placeholder="Enter detailed findings..." className="w-full p-3 min-h-[300px] text-sm border-2 border-gray-300 rounded-md focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5266" />
              </div>
              <div className="pt-6 border-t border-gray-300" data-id="element-5267">
                <div className="max-w-md" data-id="element-5268">
                  <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-5269">
                    Sonographer's Name
                  </label>
                  <input type="text" value={pelvicForm.sonographerName} onChange={e => setPelvicForm({
                ...pelvicForm,
                sonographerName: e.target.value
              })} placeholder="Enter sonographer name" className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none" data-id="element-5270" />
                </div>
              </div>
            </div> : <Card title="Radiologist Report" data-id="element-5271">
              <div className="space-y-4 h-full flex flex-col" data-id="element-5272">
                <textarea className="w-full flex-1 min-h-[200px] border border-gray-300 rounded-md p-3 focus:ring-purple-500 focus:border-purple-500" placeholder="Enter detailed report findings here..." value={report} onChange={e => setReport(e.target.value)} data-id="element-5273" />
                <div className="text-sm text-gray-500 text-right" data-id="element-5274">
                  Report by: {currentUser?.fullName}
                </div>
              </div>
            </Card>}
        </div>

        <div className="flex justify-end" data-id="element-5275">
          <Button onClick={handleSaveResults} size="lg" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isSendDisabled} data-id="element-5276">
            <CheckCircle className="w-5 h-5 mr-2" data-id="element-5277" /> Send to Clinician
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-5278">
      <div className="flex justify-between items-center" data-id="element-5279">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-5280">
          Radiology Dashboard
        </h1>
        <Badge variant="purple" className="text-sm px-3 py-1" data-id="element-5281">
          {pendingRequests.length} Pending Scans
        </Badge>
      </div>

      <Card noPadding data-id="element-5282">
        <ul className="divide-y divide-gray-200" data-id="element-5283">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.rad.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-5284">
                  <div className="flex items-center" data-id="element-5285">
                    <div className="bg-purple-100 p-2 rounded-full mr-4" data-id="element-5286">
                      <Scan className="h-6 w-6 text-purple-600" data-id="element-5287" />
                    </div>
                    <div data-id="element-5288">
                      <p className="text-sm font-medium text-blue-600" data-id="element-5289">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-5290">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm font-medium text-purple-700" data-id="element-5291">
                        {item.rad.scanType}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-purple-600 hover:bg-purple-700 text-white" data-id="element-5292">
                    Upload Scan
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-5293">
              <Scan className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-5294" />
              <p className="text-lg font-medium text-gray-900" data-id="element-5295">
                No pending scan requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};